import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Edit2, Save, X } from 'lucide-react'
import { Input, Button, Alert } from '@components/common'
import './ProfileInfoTab.css'

/**
 * Profile Update Validation Schema
 */
const profileSchema = z.object({
	first_name: z
		.string()
		.min(2, 'First name must be at least 2 characters')
		.max(100, 'First name cannot exceed 100 characters'),
	last_name: z
		.string()
		.min(2, 'Last name must be at least 2 characters')
		.max(100, 'Last name cannot exceed 100 characters'),
	phone: z
		.string()
		.max(20, 'Phone number cannot exceed 20 characters')
		.optional()
		.or(z.literal('')),
})

/**
 * Profile Info Tab Component
 *
 * View and edit personal information (first name, last name, phone).
 * Email is displayed as read-only.
 *
 * @param {Object} props - Component props
 * @param {Object} props.user - User data
 * @param {Function} props.onSave - Save handler
 * @param {boolean} props.saving - Saving state
 */
const ProfileInfoTab = ({ user, onSave, saving = false }) => {
	const [isEditing, setIsEditing] = useState(false)
	const [error, setError] = useState(null)

	const {
		register,
		handleSubmit,
		formState: { errors, isDirty },
		reset,
	} = useForm({
		resolver: zodResolver(profileSchema),
		mode: 'onChange',
		defaultValues: {
			first_name: user?.first_name || '',
			last_name: user?.last_name || '',
			phone: user?.phone || '',
		},
	})

	const handleEdit = () => {
		setIsEditing(true)
		setError(null)
	}

	const handleCancel = () => {
		setIsEditing(false)
		setError(null)
		reset({
			first_name: user?.first_name || '',
			last_name: user?.last_name || '',
			phone: user?.phone || '',
		})
	}

	const onSubmit = async (data) => {
		try {
			setError(null)
			await onSave(data)
			setIsEditing(false)
		} catch (err) {
			setError(err.message || 'Failed to update profile')
		}
	}

	return (
		<div className="profile-info-tab">
			{/* Header with Edit Button */}
			<div className="profile-info-tab__header">
				<div>
					<h3 className="profile-info-tab__title">Personal Information</h3>
					<p className="profile-info-tab__subtitle">
						Manage your personal details
					</p>
				</div>
				{!isEditing && (
					<Button variant="secondary" size="small" onClick={handleEdit}>
						<Edit2 size={14} />
						<span>Edit</span>
					</Button>
				)}
			</div>

			{error && (
				<Alert
					variant="error"
					message={error}
					dismissible
					onClose={() => setError(null)}
				/>
			)}

			{/* Form / View Mode */}
			<form onSubmit={handleSubmit(onSubmit)} className="profile-info-tab__form">
				<div className="profile-info-tab__grid">
					<div className="profile-info-tab__field">
						<label className="profile-info-tab__label">First Name</label>
						{isEditing ? (
							<Input
								{...register('first_name')}
								error={errors.first_name?.message}
								disabled={saving}
								placeholder="Enter first name"
							/>
						) : (
							<p className="profile-info-tab__value">{user?.first_name || '-'}</p>
						)}
					</div>

					<div className="profile-info-tab__field">
						<label className="profile-info-tab__label">Last Name</label>
						{isEditing ? (
							<Input
								{...register('last_name')}
								error={errors.last_name?.message}
								disabled={saving}
								placeholder="Enter last name"
							/>
						) : (
							<p className="profile-info-tab__value">{user?.last_name || '-'}</p>
						)}
					</div>

					<div className="profile-info-tab__field">
						<label className="profile-info-tab__label">Email Address</label>
						<p className="profile-info-tab__value profile-info-tab__value--readonly">
							{user?.email || '-'}
						</p>
						{isEditing && (
							<span className="profile-info-tab__hint">
								Email cannot be changed. Contact support for assistance.
							</span>
						)}
					</div>

					<div className="profile-info-tab__field">
						<label className="profile-info-tab__label">Phone Number</label>
						{isEditing ? (
							<Input
								{...register('phone')}
								type="tel"
								error={errors.phone?.message}
								disabled={saving}
								placeholder="Enter phone number"
							/>
						) : (
							<p className="profile-info-tab__value">{user?.phone || 'Not set'}</p>
						)}
					</div>
				</div>

				{/* Action Buttons */}
				{isEditing && (
					<div className="profile-info-tab__actions">
						<Button
							type="button"
							variant="secondary"
							onClick={handleCancel}
							disabled={saving}
						>
							<X size={16} />
							<span>Cancel</span>
						</Button>
						<Button
							type="submit"
							variant="primary"
							loading={saving}
							disabled={!isDirty}
						>
							<Save size={16} />
							<span>Save Changes</span>
						</Button>
					</div>
				)}
			</form>
		</div>
	)
}

ProfileInfoTab.propTypes = {
	user: PropTypes.shape({
		first_name: PropTypes.string,
		last_name: PropTypes.string,
		email: PropTypes.string,
		phone: PropTypes.string,
	}),
	onSave: PropTypes.func.isRequired,
	saving: PropTypes.bool,
}

export default ProfileInfoTab
